import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Chip, IconButton } from '@mui/material';
import { ReactComponent as BackArrow } from '../../assets/icons/back-arrow.svg';
import { OptionsMenuImg } from '../../assets/icons';
import GalleryTopBar from '../../components/GalleryTopBar';
import ContextMenu from '../../components/ContextMenu';
import { ContextMenuItemProps, ContextMenuRef } from '../../components/ContextMenu/types';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import { useNewspaperStore } from '../../hooks/zustand/newspapers';
import * as NewspapersAPI from '../../api/Newspapers';
import CreateEditionDialog from '../../components/dialogs/CreateEditionDialog';
import { Edition } from '../../types/models/Newspaper';

const STATUS_COLOR: Record<string, string> = {
  draft: '#888',
  open: '#2e7d32',
  published: '#1565c0',
};

function EditionCard({ edition, newspaperId, onDeleted }: {
  edition: Edition;
  newspaperId: string;
  onDeleted: () => void;
}) {
  const navigate = useNavigate();
  const contextMenuRef = useRef<ContextMenuRef>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const menuItems = useMemo<ContextMenuItemProps[]>(() => [
    {
      label: 'Delete',
      icon: 'BIN',
      onClick: () => setIsDeleteOpen(true),
      style: { color: '#FF5252 !important' },
    },
  ], []);

  const handleDropdownClick = (e: any) => {
    e.stopPropagation();
    contextMenuRef.current?.openMenu(e);
  };

  const handleDelete = async () => {
    await NewspapersAPI.deleteEdition(newspaperId, edition.id);
    onDeleted();
  };

  return (
    <>
      <ContextMenu menuItems={menuItems} ref={contextMenuRef}>
        <Card
          onClick={() => navigate(`/newspaper/${newspaperId}/edition/${edition.id}`)}
          elevation={4}
          sx={{
            width: 200,
            height: 140,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            padding: '1rem',
            background: '#163567',
            color: '#fff',
            cursor: 'pointer',
            position: 'relative',
            '&:hover': { background: '#1e4a8c' },
          }}
        >
          <IconButton
            onClick={handleDropdownClick}
            sx={{ position: 'absolute', top: 4, right: 4, p: 0.5 }}
          >
            <img src={OptionsMenuImg} alt="options" style={{ filter: 'brightness(10)' }} />
          </IconButton>
          <div style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2, marginBottom: 8, marginTop: 4 }}>
            {edition.name}
          </div>
          <Chip
            label={edition.status}
            size="small"
            sx={{
              width: 'fit-content',
              color: '#fff',
              borderColor: STATUS_COLOR[edition.status] ?? '#fff',
              background: STATUS_COLOR[edition.status] ?? 'transparent',
              fontSize: '0.65rem',
            }}
          />
        </Card>
      </ContextMenu>
      <DeleteConfirmationDialog
        title="Delete edition?"
        description={`Are you sure you want to delete "${edition.name}"? This will also delete all stories in this edition.`}
        onSubmit={handleDelete}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      />
    </>
  );
}

export default function NewspaperPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const currentNewspaper = useNewspaperStore((state) => state.currentNewspaper);
  const setCurrentNewspaper = useNewspaperStore((state) => state.setCurrentNewspaper);
  const currentEditions = useNewspaperStore((state) => state.currentEditions);
  const loadEditions = useNewspaperStore((state) => state.loadEditions);
  const newspapers = useNewspaperStore((state) => state.newspapers);

  const [isCreateEditionOpen, setIsCreateEditionOpen] = useState(false);

  useEffect(() => {
    if (id) {
      const found = newspapers.find((n) => n.id === id);
      if (found) setCurrentNewspaper(found);
      loadEditions(id);
    }
    return () => setCurrentNewspaper(undefined);
  }, [id]);

  window.document.title = currentNewspaper?.name ?? 'Newspaper';

  return (
    <>
      <GalleryTopBar tab="newspapers" />
      <div style={{ background: '#F0F0F4', minHeight: 'calc(100vh - 64px)', padding: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <IconButton onClick={() => navigate('/gallery/newspapers')} size="small">
            <BackArrow height={20} width={20} stroke="#163567" />
          </IconButton>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#163567' }}>
              {currentNewspaper?.name ?? '…'}
            </div>
            {currentNewspaper && (
              <Chip
                label={currentNewspaper.scope_type}
                size="small"
                sx={{ mt: 0.5, color: '#163567', borderColor: '#163567' }}
                variant="outlined"
              />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <Card
            onClick={() => setIsCreateEditionOpen(true)}
            elevation={4}
            sx={{
              width: 200,
              height: 140,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: 'unset',
              border: '2px dashed #163567',
              color: '#163567',
              fontWeight: 600,
              fontSize: '0.9rem',
              gap: '0.5rem',
              '&:hover': { background: '#e8edf5' },
            }}
          >
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>+</span>
            New edition
          </Card>

          {currentEditions.map((edition) => (
            <EditionCard
              key={edition.id}
              edition={edition}
              newspaperId={id!}
              onDeleted={() => loadEditions(id!)}
            />
          ))}
        </div>
      </div>

      {id && (
        <CreateEditionDialog
          isOpen={isCreateEditionOpen}
          newspaperId={id}
          onClose={() => setIsCreateEditionOpen(false)}
        />
      )}
    </>
  );
}
